// frontend/src/features/livestock/organismSearch.ts
// Search and order for the assign view's organism list (plan 4.4): common
// name, scientific name, subcategory and vendor. Alive organisms come first;
// deceased and sold ones stay listed below them, since back-filled photos often
// show organisms that have died since.

import type { Livestock, LivestockStatus, LivestockType } from '@/types/livestock';
import { LIVESTOCK_TYPE_META } from './constants';

const STATUS_RANK: Record<LivestockStatus, number> = {
  alive: 0,
  unknown: 1,
  sold: 2,
  deceased: 3,
};

const TYPE_ORDER = Object.keys(LIVESTOCK_TYPE_META) as LivestockType[];

/** Lower case, without accents, single spaces. */
function normalize(text: string | null | undefined): string {
  return (text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function words(text: string): string[] {
  return text.split(/[^a-z0-9]+/).filter(Boolean);
}

/**
 * How well the organism matches: 0 its name starts with the query, 1 every
 * word starts a word of its name, 2 every word is in its name, 3 in its name
 * or scientific name, 4 in any searched field. null when it doesn't match.
 */
function matchRank(item: Livestock, query: string, tokens: string[]): number | null {
  const name = normalize(item.common_name);
  if (name.startsWith(query)) return 0;
  const nameWords = words(name);
  if (tokens.every((token) => nameWords.some((word) => word.startsWith(token)))) return 1;
  if (tokens.every((token) => name.includes(token))) return 2;
  const withScientific = `${name} ${normalize(item.scientific_name)}`;
  if (tokens.every((token) => withScientific.includes(token))) return 3;
  const everything = `${withScientific} ${normalize(item.subcategory)} ${normalize(item.vendor)}`;
  if (tokens.every((token) => everything.includes(token))) return 4;
  return null;
}

/** The organisms of this type matching the query, best first. */
export function rankOrganisms(
  livestock: Livestock[],
  query: string,
  type: LivestockType | null
): Livestock[] {
  const normalized = normalize(query);
  const tokens = normalized.split(' ').filter(Boolean);
  const ranked: { item: Livestock; rank: number }[] = [];
  for (const item of livestock) {
    if (type && item.livestock_type !== type) continue;
    const rank = tokens.length ? matchRank(item, normalized, tokens) : 0;
    if (rank !== null) ranked.push({ item, rank });
  }
  return ranked
    .sort(
      (a, b) =>
        (STATUS_RANK[a.item.status] ?? 9) - (STATUS_RANK[b.item.status] ?? 9) ||
        a.rank - b.rank ||
        a.item.common_name.localeCompare(b.item.common_name) ||
        a.item.id - b.item.id
    )
    .map(({ item }) => item);
}

/** Words that ask for a tank shot: a search starting with one puts it first. */
const TANK_SHOT_KEYWORDS = ['tank', 'fts', 'full', 'whole'];

/** Phrases whose start (two letters or more) lists the tank shot after the organisms. */
const TANK_SHOT_TERMS = ['tank shot', 'fts', 'full tank shot', 'whole tank shot'];

/** Where "Tank shot" goes among the search's results. */
export type TankShotPlace = 'first' | 'after';

/**
 * Where the search puts "Tank shot" (tank shots design 6.1), case and accents
 * ignored: first when it starts with "tank", "fts", "full" or "whole" ("full
 * tank", "tanks"); after the organisms when it is two letters or more of
 * "tank shot", "fts", "full tank shot" or "whole tank shot" without a whole
 * keyword ("ta", "fu", "wh"); not listed otherwise, single letters included
 * (the chip is always there).
 */
export function tankShotPlace(query: string): TankShotPlace | null {
  const normalized = normalize(query);
  if (!normalized) return null;
  if (TANK_SHOT_KEYWORDS.some((keyword) => normalized.startsWith(keyword))) return 'first';
  if (normalized.length >= 2 && TANK_SHOT_TERMS.some((term) => term.startsWith(normalized))) {
    return 'after';
  }
  return null;
}

/** The livestock types this list has, in the usual order. */
export function livestockTypesIn(livestock: Livestock[]): LivestockType[] {
  const present = new Set(livestock.map((item) => item.livestock_type));
  return TYPE_ORDER.filter((type) => present.has(type));
}
